import * as R from 'ramda';
import { put, call, select, takeLatest } from 'redux-saga/effects';
// components
import { closeModal } from '../../components/modal/actions';
import { closeLoader, openLoader } from '../../components/loader/actions';
import { transformSearchCriteriaBeforeReportPost } from '../../components/edit-report/helpers';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// report-common
import { getReportSagas } from '../../report-common';
// sagas
import { visitPageSaga } from '../../sagas';
// utilities
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
// feature compensation-group
import * as A from './actions';
import {
  makeSelectDriverGuid,
  makeSelectPagination,
  makeSelectUsedReport,
  makeSelectVendorGuid,
  makeSelectTitleSortValues,
  makeSelectAvailableReports,
  makeSelectTableTitleFilters,
} from './selectors';
//////////////////////////////////////////////////

function* getItemListRequest(action) {
  try {
    yield put(A.setListLoading(true));

    const reportParams = yield select(makeSelectUsedReport());
    const availableReports = yield select(makeSelectAvailableReports());

    if (R.and(
      G.isNilOrEmpty(availableReports),
      G.isNilOrEmpty(G.getPropFromObject('fields', reportParams)),
    )) {
      yield put(A.setListLoading(false));

      return yield put(closeLoader());
    }

    const driverGuid = yield select(makeSelectDriverGuid());
    const pagination = yield select(makeSelectPagination());
    const fleetVendorGuid = yield select(makeSelectVendorGuid());
    const titleOrderFields = yield select(makeSelectTitleSortValues());
    const titleFilterParams = yield select(makeSelectTableTitleFilters());

    const currentBranchGuid = G.getAmousCurrentBranchGuidFromWindow();

    const orderFields = G.ifElse(
      G.isNotEmpty(titleOrderFields),
      R.values(titleOrderFields),
      G.getOrElse(reportParams, 'orderFields', []),
    );

    const searchCriteria = G.getSearchCriteriaWithTitleFilters(reportParams, titleFilterParams);

    const guids = R.pathOr(null, ['payload', 'guids'], action);

    let systemFields;

    if (R.isNotNil(guids)) {
      systemFields = [
        { sequence: 0, name: GC.FIELD_NAME },
        { sequence: 1, name: GC.FIELD_ACTIVE },
        { sequence: 2, name: GC.FIELD_VERSION },
        { sequence: 3, name: GC.FIELD_LAST_MODIFIED_BY },
        { sequence: 4, name: GC.FIELD_LAST_MODIFIED_DATE },
        { sequence: 5, collection: true, name: 'compensations' },
      ];
    }

    const { limit, offset } = pagination;

    const reqBody = {
      guids,
      limit,
      offset,
      orderFields,
      systemFields,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      fields: G.getOrElse(reportParams, 'fields', []),
      searchCriteria: transformSearchCriteriaBeforeReportPost(searchCriteria),
    };

    let options = {
      data: reqBody,
    };

    if (G.isNotNilAndNotEmpty(driverGuid)) {
      const params = { driverGuid };

      options = R.assoc('params', params, options);
    }

    if (G.isNotNilAndNotEmpty(fleetVendorGuid)) {
      const params = { fleetVendorGuid };

      options = R.assoc('params', params, options);
    }

    const res = yield call(sendRequest, 'post', endpointsMap.compensationGroupList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getItemListSuccess({ data, guids }));
    } else {
      yield call(G.handleFailResponse, res, 'getItemListRequest fail');
    }

    yield put(closeLoader());
    yield put(A.setListLoading(false));
  } catch (error) {
    yield put(closeLoader());
    yield put(A.setListLoading(false));

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'getItemListRequest exception');
  }
}

function* createOrUpdateCompensationGroupRequest({ payload }) {
  try {
    const { version, compensations } = payload;

    if (G.isNilOrEmpty(compensations)) {
      return yield call(G.showToastrMessage, 'info', 'validation:compensations-are-required');
    }

    yield put(openLoader());

    const currentBranchGuid = G.getAmousCurrentBranchGuidFromWindow();

    const method = G.ifElse(R.isNil(version), 'post', 'put');
    const mappedCompensations = R.map(R.omit(['origins', 'destinations']), compensations);

    const options = {
      data: {
        ...payload,
        compensations: mappedCompensations,
        [GC.BRANCH_GUID]: R.propOr(currentBranchGuid, GC.BRANCH_GUID, payload),
      },
    };

    const res = yield call(sendRequest, method, endpointsMap.compensationGroup, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      const compensationGroupGuidToReload = R.pathOr(R.prop(GC.FIELD_GUID, payload), ['data', GC.FIELD_GUID], res);

      yield put(closeModal());
      yield put(A.getItemListRequest({ guids: R.of(Array, compensationGroupGuidToReload) }));

      yield call(G.showToastrMessage, 'success', 'toastr:success:201');
    } else {
      yield call(G.handleFailResponse, res, 'createOrUpdateCompensationGroupRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'createOrUpdateCompensationGroupRequest exception');
  }
}

function* removeCompensationGroupRequest({ payload }) {
  try {
    yield put(openLoader());

    const res = yield call(sendRequest, 'delete', endpointsMap.compensationGroupByGuid(payload));

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(closeModal());
      yield put(A.deleteItemSuccess(payload));
    } else {
      yield call(G.handleFailResponse, res, 'removeCompensationGroupRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'removeCompensationGroupRequest exception');
  }
}

function* createOrUpdateCompensationGroupCompensationRequest({ payload }) {
  try {
    yield put(openLoader());

    const { version, compensationGroupGuid, additionalOptions = {} } = payload;

    const values = R.dissoc('additionalOptions', payload);
    const method = G.ifElse(R.isNil(version), 'post', 'put');
    const options = { data: values };

    const res = yield call(sendRequest, method, endpointsMap.compensationGroupCompensation, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      const { shouldCloseModal, successCallback } = additionalOptions;

      if (shouldCloseModal) yield put(closeModal());

      yield put(A.getItemListRequest({ guids: R.of(Array, compensationGroupGuid) }));

      G.callFunction(successCallback);

      yield call(G.showToastrMessage, 'success', 'toastr:success:201');
    } else {
      yield call(G.handleFailResponse, res, 'createOrUpdateCompensationGroupCompensationRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'createOrUpdateCompensationGroupCompensationRequest exception');
  }
}

function* removeCompensationGroupCompensationRequest({ payload }) {
  try {
    yield put(openLoader());

    const { guid } = payload;

    const res = yield call(sendRequest, 'delete', endpointsMap.compensationGroupCompensationByGuid(guid));

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(closeModal());
      yield put(A.removeCompensationGroupCompensationSuccess(payload));
    } else {
      yield call(G.handleFailResponse, res, 'removeCompensationGroupCompensationRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'removeCompensationGroupCompensationRequest exception');
  }
}

const createUpdateReportSuccessCallback = (data) => G.getReportSortedBySeqFreez(data);

const {
  handleAvailableReportsRequest,
  handleCreateReportRequestSaga,
  handleUpdateReportRequestSaga,
  handleChangeDefaultReportSaga,
} = getReportSagas(
  GC.FLEET_COMPENSATION_GROUP_REPORT,
  A,
  getItemListRequest,
  { createUpdateReportSuccessCallback },
);

function* visitCompensationGroupOnFleetProfilePage({ payload }) {
  while (true) { // eslint-disable-line
    yield put(openLoader());
    yield put(A.setInitialState());

    const { driverGuid, fleetVendorGuid } = payload;

    yield put(A.setReportPending());

    if (driverGuid) yield put(A.setDriverGuid(driverGuid));

    if (fleetVendorGuid) yield put(A.setVendorGuid(fleetVendorGuid));

    yield call(handleAvailableReportsRequest, { payload: { [GC.REPORT_TYPE]: GC.FLEET_COMPENSATION_GROUP_REPORT } });
    yield call(getItemListRequest);

    yield put(closeLoader());

    break;
  }
}

function* visitCompensationGroupPageSaga({ payload }) {
  while (true) { // eslint-disable-line
    yield put(A.setInitialState());

    yield call(visitPageSaga, payload, GC.CHECK_VISIT_COMPENSATION_GROUP_PAGE);

    yield put(openLoader());
    yield put(A.setReportPending());

    yield call(handleAvailableReportsRequest, { payload });
    yield call(getItemListRequest);

    yield put(closeLoader());

    break;
  }
}

export function* compensationGroupWatcherSaga() {
  yield takeLatest(GC.VISIT_COMPENSATION_GROUP_PAGE, visitCompensationGroupPageSaga);
  // report
  yield takeLatest(A.getItemListRequest, getItemListRequest);
  yield takeLatest(A.createReportRequest, handleCreateReportRequestSaga);
  yield takeLatest(A.updateReportRequest, handleUpdateReportRequestSaga);
  yield takeLatest(A.changeDefaultReportRequest, handleChangeDefaultReportSaga);
  yield takeLatest(A.removeCompensationGroupRequest, removeCompensationGroupRequest);
  // compensation-group
  yield takeLatest(A.createOrUpdateCompensationGroupRequest, createOrUpdateCompensationGroupRequest);
  yield takeLatest(A.visitCompensationGroupOnFleetProfilePage, visitCompensationGroupOnFleetProfilePage);
  yield takeLatest(A.removeCompensationGroupCompensationRequest, removeCompensationGroupCompensationRequest);
  yield takeLatest(A.createOrUpdateCompensationGroupCompensationRequest, createOrUpdateCompensationGroupCompensationRequest);
}

export default compensationGroupWatcherSaga;

export {
  getItemListRequest,
  visitCompensationGroupPageSaga,
  removeCompensationGroupRequest,
  createOrUpdateCompensationGroupRequest,
  visitCompensationGroupOnFleetProfilePage,
  removeCompensationGroupCompensationRequest,
  createOrUpdateCompensationGroupCompensationRequest,
};
